/**
 * Central source of truth for the locales that have maintained catalog/PLP
 * translations. Keyed by `i18n.language` (which equals the user's
 * `preferredLanguage` preference code, e.g. `ja`, `es`, `pt-BR`).
 *
 * Each locale maps to:
 *  - `algoliaSuffix`: the suffix used on Algolia catalog fields produced by
 *    search-workers (e.g. `title_ja`, `summary_pt_br`, `skill_names_es`).
 *  - `maintainedTranslationCode`: the value stored in the Algolia
 *    `maintained_translations` attribute (lowercase, hyphenated) and used as
 *    the catalog `language` filter value.
 */
import { languagePreferenceOptions } from '~/features/internationalization/languages';

export interface TranslatedLocaleConfig {
  localeCode: string;
  algoliaSuffix: string;
  maintainedTranslationCode: string;
}

export const TRANSLATED_LOCALE_CONFIGS: Record<string, TranslatedLocaleConfig> = {
  ar: { localeCode: 'ar', algoliaSuffix: 'ar', maintainedTranslationCode: 'ar' },
  ja: { localeCode: 'ja', algoliaSuffix: 'ja', maintainedTranslationCode: 'ja' },
  es: { localeCode: 'es', algoliaSuffix: 'es', maintainedTranslationCode: 'es' },
  de: { localeCode: 'de', algoliaSuffix: 'de', maintainedTranslationCode: 'de' },
  it: { localeCode: 'it', algoliaSuffix: 'it', maintainedTranslationCode: 'it' },
  fr: { localeCode: 'fr', algoliaSuffix: 'fr', maintainedTranslationCode: 'fr' },
  'pt-BR': { localeCode: 'pt-BR', algoliaSuffix: 'pt_br', maintainedTranslationCode: 'pt-br' },
  'pt-PT': { localeCode: 'pt-PT', algoliaSuffix: 'pt_pt', maintainedTranslationCode: 'pt-pt' },
  ru: { localeCode: 'ru', algoliaSuffix: 'ru', maintainedTranslationCode: 'ru' },
};

export const TRANSLATED_LOCALE_CODES = Object.keys(TRANSLATED_LOCALE_CONFIGS);

/** BCP-47 variants stored in user-api that map to a translated locale code. */
const BCP47_TO_TRANSLATED_LOCALE_CODE: Record<string, string> = {
  'ar-SA': 'ar',
  'de-DE': 'de',
  'es-ES': 'es',
  'fr-FR': 'fr',
  'it-IT': 'it',
  'ja-JP': 'ja',
  'ru-RU': 'ru',
};

function resolveTranslatedLocaleCode(localeCode: string): string {
  if (localeCode in TRANSLATED_LOCALE_CONFIGS) {
    return localeCode;
  }

  return BCP47_TO_TRANSLATED_LOCALE_CODE[localeCode] ?? localeCode;
}

/** A single locale's translated title/summary for a catalog item. */
export interface CatalogItemTranslation {
  title: string | null;
  summary: string | null;
}

/** Map of locale code (`i18n.language`) to its translated title/summary. */
export type CatalogItemTranslations = Record<string, CatalogItemTranslation>;

/**
 * Build the `translations` map for a catalog item from raw Algolia fields
 * (`title_<suffix>` / `summary_<suffix>`), keyed by locale code.
 */
export function buildCatalogItemTranslations(source: Record<string, unknown>): CatalogItemTranslations {
  const asString = (value: unknown): string | null => (typeof value === 'string' && value ? value : null);

  const translations: CatalogItemTranslations = {};
  for (const config of Object.values(TRANSLATED_LOCALE_CONFIGS)) {
    translations[config.localeCode] = {
      title: asString(source[`title_${config.algoliaSuffix}`]),
      summary: asString(source[`summary_${config.algoliaSuffix}`]),
    };
  }
  return translations;
}

export const MAINTAINED_TRANSLATION_CODES = Object.values(TRANSLATED_LOCALE_CONFIGS).map(
  (config) => config.maintainedTranslationCode
);

export function getTranslatedLocaleConfig(localeCode?: string | null): TranslatedLocaleConfig | null {
  if (!localeCode) {
    return null;
  }

  const resolvedCode = resolveTranslatedLocaleCode(localeCode);
  return TRANSLATED_LOCALE_CONFIGS[resolvedCode] ?? null;
}

export function isTranslatedLocale(localeCode?: string | null): boolean {
  return Boolean(getTranslatedLocaleConfig(localeCode));
}

/**
 * Resolve the user-facing label for a translated locale's catalog language
 * filter option (e.g. `Espanol`, `日本語`). Normalizes BCP-47 inputs such as
 * `de-DE` to the maintained locale code before the label lookup, so the option
 * never silently disappears when `i18n.language` is a full BCP-47 variant.
 */
export function getTranslatedLanguageLabel(localeCode?: string | null): string | undefined {
  const config = getTranslatedLocaleConfig(localeCode);
  if (!config) {
    return undefined;
  }

  return languagePreferenceOptions.find((option) => option.value === config.localeCode)?.label;
}

/** Resolve the active translated locale for catalog/PLP content. */
export function resolveActiveTranslatedLocaleCode(
  preferredLanguage?: string | null,
  i18nLanguage?: string | null,
  cookieLocale?: string | null
): string | null {
  // Account preference wins (matches production SSR), then NEXT_LOCALE cookie, then i18n.
  return (
    getTranslatedLocaleConfig(preferredLanguage)?.localeCode ??
    getTranslatedLocaleConfig(cookieLocale)?.localeCode ??
    getTranslatedLocaleConfig(i18nLanguage)?.localeCode ??
    null
  );
}

export function getCatalogItemLocaleFields(
  catalogItem:
    | {
        translations?: CatalogItemTranslations;
        title?: string | null;
        summary?: string | null;
      }
    | undefined,
  localeCode: string
): { title?: string; summary?: string } {
  const translation = catalogItem?.translations?.[localeCode];
  return {
    title: translation?.title ?? undefined,
    summary: translation?.summary ?? undefined,
  };
}

/** Resolve translated locale for SSR from user-api preference and NEXT_LOCALE cookie. */
export function resolveServerTranslatedLocaleConfig(
  cookieLocale?: string | null,
  preferredLanguage?: string | null
): TranslatedLocaleConfig | null {
  const localeCode = resolveActiveTranslatedLocaleCode(preferredLanguage, undefined, cookieLocale);
  return localeCode ? getTranslatedLocaleConfig(localeCode) : null;
}
